import { DropResult } from "@hello-pangea/dnd";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GraphQLClient } from "../../../../../../shared/gql-client";
import RouteService from "../../service/routeService";
import RequestService from "../../service/requestService";
import {
  addDealerToRoute,
  addRequestToRoute,
  moveRequestToRoute,
  moveSlipToRoute,
  PlanningSliceModel,
  removeRequestFromRoute,
  removeSlipFromRoute,
} from "../PlanningSlice";
import PackingSlipService from "../../service/packingSlipService";

const routeApi = new RouteService();
const requestApi = new RequestService();
const packingSlipAPI = new PackingSlipService();

export const planningDragEnd = createAsyncThunk(
  "planning/dragEnd",
  async (result: DropResult, thunkAPI) => {
    if (!result.destination) return;

    const { client } = thunkAPI.extra as { client: GraphQLClient };

    try {
      routeApi.setClient(client);
      requestApi.setClient(client);
      packingSlipAPI.setClient(client);

      const { planning } = thunkAPI.getState() as {
        planning: PlanningSliceModel;
      };

      if (result.destination.droppableId == "remove-items") {
        if (result.draggableId.startsWith("request:")) {
          thunkAPI.dispatch(removeRequestFromRoute(result));

          const draggable = result.draggableId;
          const requestId = draggable.replace("request:", "");

          return await requestApi.removeRequestsFromRoute([requestId]);
        }

        if (result.draggableId.startsWith("slip:")) {
          thunkAPI.dispatch(removeSlipFromRoute(result));

          const draggable = result.draggableId;
          const slipId = draggable.replace("slip:", "");

          const packingSlip = planning.routes
            .flatMap((r) => r.packingSlips)
            .find((slip) => slip.id === slipId);

          if (!packingSlip) {
            // void thunkAPI.dispatch(getTrucksWithRoutes());
            return thunkAPI.rejectWithValue({
              message: "Could not find packing slip:" + slipId + " to move",
            });
          }

          if (packingSlip.status === "completed") {
            return await packingSlipAPI.mutate({
              id: {
                type: "ID!",
                value: slipId,
              },
              date: {
                type: "Date!",
                value: packingSlip.date,
              },
              relation_id: {
                type: "ID!",
                value: packingSlip.relation.id,
              },
              status: {
                type: "String!",
                value: packingSlip.status,
              },
              route_id: {
                type: "ID",
                value: null,
              },
            });
          }

          return await packingSlipAPI.delete(slipId);
        }

        return thunkAPI.rejectWithValue({
          message: "Could not remove requests to route",
        });
      }

      const destination = result.destination.droppableId;
      const routeId = destination.replace("route:", "");
      const route = planning.routes.find((r) => r.id === routeId);

      if (!route) {
        // void thunkAPI.dispatch(getTrucksWithRoutes());
        return thunkAPI.rejectWithValue({
          message: "Could not find the route with id: " + routeId,
        });
      }

      if (result.draggableId.startsWith("new-request:")) {
        thunkAPI.dispatch(addRequestToRoute(result));

        const draggable = result.draggableId;
        const requestId = draggable.replace("new-request:", "");

        return await routeApi.connectRequestsToRoute([requestId], route, 0);
      }

      if (result.draggableId.startsWith("new-dealer:")) {
        thunkAPI.dispatch(addDealerToRoute(result));

        const draggable = result.draggableId;
        const dealerId = draggable.replace("new-dealer:", "");

        const requestIds: string[] = [];

        const dealer = planning.dealers.find(
          (d) => d.relation.id.toString() === dealerId,
        );

        if (!dealer) {
          // void thunkAPI.dispatch(getTrucksWithRoutes());
          return thunkAPI.rejectWithValue({
            message: "Could not find dealer:" + dealerId + " to add",
          });
        }

        requestIds.push(...dealer.deliver_requests.map((r) => r.id));
        const pickup_amount = dealer.pickup_amount;

        return await routeApi.connectRequestsToRoute(
          requestIds,
          route,
          pickup_amount ?? 0,
          result.destination.index,
          dealer.relation.id,
        );
      }

      if (result.draggableId.startsWith("request:")) {
        thunkAPI.dispatch(moveRequestToRoute(result));

        const draggable = result.draggableId;
        const requestId = draggable.replace("request:", "");

        return await routeApi.connectRequestsToRoute([requestId], route, 0);
      }

      if (result.draggableId.startsWith("slip:")) {
        thunkAPI.dispatch(moveSlipToRoute(result));

        const draggable = result.draggableId;
        const slipId = draggable.replace("slip:", "");

        const packingSlip = planning.routes
          .flatMap((r) => r.packingSlips)
          .find((slip) => slip.id === slipId);

        if (!packingSlip) {
          // void thunkAPI.dispatch(getTrucksWithRoutes());
          return thunkAPI.rejectWithValue({
            message: "Could not find packing slip:" + slipId + " to move",
          });
        }

        return await routeApi.connectPackingSlipToRoute(
          route.id,
          packingSlip.id,
          result.destination.index,
        );
      }

      // void thunkAPI.dispatch(getTrucksWithRoutes());
      return thunkAPI.rejectWithValue({
        message: "Could not connect requests to route",
      });
    } catch (error) {
      //void thunkAPI.dispatch(getTrucksWithRoutes());
      return thunkAPI.rejectWithValue(error);
    }
  },
);
